<template>
  <SectionTemplate
    title="赋能全行业，有言PPT Speak让知识主动“对话”"
    desc="上传您的PPT，即刻获得专属的、可深度交互的智能讲解与答疑助手。"
    class="section-industry"
  >
    <div class="industry-container">
      <MarqueeSwiper :items="items" class="marquee-slide" />
    </div>

    <div class="section-industry--footer">
      <StartBtn :handler="handler" text="免费试用" />
      <BookBtn :code="1078924" text="企业咨询" />
    </div>
  </SectionTemplate>
</template>

<script lang="ts" setup>
import BookBtn from '~/design/components/buttons/book-btn.vue';
import StartBtn from '~/design/components/buttons/start-btn.vue';
import MarqueeSwiper from '~/design/components/marquee-swiper/index.vue';
import SectionTemplate from '~/design/widgets/landing-section/cn-template.vue';

const props = defineProps<{
  handler: () => void;
}>();
const academyLinks = {
  'real-avatar': 'https://www.youyan3d.com/blog/One-Click-Edit-Hyperrealistic-3D-avatars',
  'ai-video': 'https://www.youyan3d.com/blog/Easily-Make-Hollywood-Level-High-Quality-Videos',
  'ai-animation':
    'https://www.youyan3d.com/blog/Text-to-3D-Animation-Generate-AI-Digital-Human-Animation-by-Input-Script',
  'ai-tts':
    'https://www.youyan3d.com/blog/Supernatural-Multistyle-Text-to-Language-for-Professional-Scenarios',
  'ai-camera': 'https://www.youyan3d.com/blog/Text-to-3D-Camera-Movement-Auto-Generate',
  'ai-script': 'https://www.youyan3d.com/blog/AI-Text-to-Text-Solve-Professional-Script-Creation',
  'ai-clone': 'https://www.youyan3d.com/blog/Avatar-Cloning-and-Voice-Cloning.html',
};
function getStrong(text: string): string {
  return `<span class="text-[var(--text-1)]">${text}</span>`;
}
const items = [
  {
    img: getFeUrl(
      '/youyan-platform/chat-ppt/use-cases/accurate-interpretation-of-policies.jpg?x-oss-process=image/format,webp',
    ),
    title: '政策精准解读',
    desc: `${getStrong('政务PPT生成可互动宣讲智能体：')}让政策宣传更易懂、更亲切。上传解读文件PPT，创建智能宣传员。市民扫码可用语音提问，数字人以口语化方式精准解答，提升政策传达效果。`,
    bgcolor: 'rgba(255, 241, 240, 1)',
    imgAlt: '有言提供超低门槛的数字人克隆服务',
    // link: academyLinks['ai-clone'],
    linkDesc: '快速克隆专属3D数字人',
  },
  {
    img: getFeUrl(
      '/youyan-platform/chat-ppt/use-cases/ai-teaching-assistant.jpg?x-oss-process=image/format,webp',
    ),
    title: 'AI助教/老师',
    desc: `${getStrong('课件PPT生成个性化答疑助手：')}无需老师1对1反复回答学生问题。上传教学课件PPT即可创建专属AI助教。学生课后提问，数字人根据学生实际问题和学习情况，调取课件进行讲解，实现个性化学习支持。`,
    bgcolor: 'rgba(236, 247, 245, 1)',
    // link: academyLinks['ai-video'],
    linkDesc: '轻松制作好莱坞级高质量视频',
    imgAlt: '在有言中输入文案就可以生成3D数字人视频',
  },
  {
    img: getFeUrl(
      '/youyan-platform/chat-ppt/use-cases/ai-product-guide.jpg?x-oss-process=image/format,webp',
    ),
    title: 'AI产品导购',
    desc: `${getStrong('复杂产品PPT变身智能讲解员：')}产品型号功能复杂，传统导购讲不清楚怎么办？上传产品详解PPT，生成智能导购助手。消费者即可随时提问产品差异、使用技巧，数字人精准演示与解答，提升购物体验与转化。`,
    bgcolor: 'rgba(255, 247, 229, 1)',
    // link: academyLinks['ai-animation'],
    linkDesc: '输入脚本即可生成AI数字人动画',
    imgAlt: '有言可以基于语义识别让数字人更加生动',
  },
  {
    img: getFeUrl(
      '/youyan-platform/chat-ppt/use-cases/post-treatment-rehabilitation-consultant.jpg?x-oss-process=image/format,webp',
    ),
    title: 'AI医疗康复顾问',
    desc: `${getStrong('康复指南PPT变身AI康复顾问：')}为医生分流常见疗后咨询。上传疾病康复管理PPT，创建患者随访助手。患者可随时询问康复注意事项，数字人依据指南权威解答，提升患者管理效率，为患者提供私人健康顾问服务。`,
    bgcolor: 'rgba(243, 244, 255, 1)',
    // link: academyLinks['ai-tts'],
    linkDesc: 'AI生成语音能够适配各种专业场景',
    imgAlt: '在有言输入文字，AI即可生成像真人一样的声音',
  },
  {
    img: getFeUrl(
      '/youyan-platform/chat-ppt/use-cases/ai-sales-expert.jpg?x-oss-process=image/format,webp',
    ),
    title: 'AI销售专家',
    desc: `${getStrong('用PPT打造智能销售讲解员：')}让销售告别耗时的基础答疑。上传产品方案PPT，生成可分享的智能助手。分享二维码添加至个人名片，客户随时扫码，既能看讲解，又能深入提问，数字人实时演示对应页面，促进转化。`,
    bgcolor: 'rgba(240, 242, 245, 1)',
    // link: academyLinks['ai-camera'],
    linkDesc: '有言能够AI生成多机位多角度的镜头',
    imgAlt: '有言能够在视频中自动生成多个机位和镜头',
  },
  {
    img: getFeUrl(
      '/youyan-platform/chat-ppt/use-cases/intelligent-investment-advisory-assistant.jpg?x-oss-process=image/format,webp',
    ),
    title: '智能投顾助手',
    desc: `${getStrong('PPT自动化解答理财基础问题：')}赋能理财投顾，服务更多客户。上传理财知识PPT，制作轻量化智能助手。客户可随时扫码咨询基础问题，数字人专业解答，让投顾更专注于高价值服务。`,
    bgcolor: 'rgba(240, 249, 255, 1)',
    // link: academyLinks['real-avatar'],
    linkDesc: '从五官到妆造，一键编辑超写实3D数字人',
    imgAlt: '有言提供高质量的超写实数字人',
  },

  {
    img: getFeUrl(
      '/youyan-platform/chat-ppt/use-cases/ai-legal-advisor.jpg?x-oss-process=image/format,webp',
    ),
    title: 'AI法律顾问',
    desc: `${getStrong('法律PPT自动化解答常见咨询：')}高效筛选与服务潜在客户。上传常见法律问题PPT，生成智能咨询助手。客户可先行获得基础问题分析与指引，提升律所专业形象与转化效率。`,
    bgcolor: 'rgba(240, 242, 245, 1)',
    // link: academyLinks['ai-script'],
    linkDesc: 'AI文生文，搞定专业脚本创作',
    imgAlt: '有言提供AI优化视频脚本的功能',
  },
  {
    img: getFeUrl(
      '/youyan-platform/chat-ppt/use-cases/ai-enterprise-management-assistant.jpg?x-oss-process=image/format,webp',
    ),
    title: 'AI企业管理助手',
    desc: `${getStrong('PPT秒变24小时智能答疑助手：')}让HR和管理团队告别重复答疑和赋能。上传公司制度和资料PPT，即可生成嵌入内网的智能助手。员工扫码即可随时提问，数字人精准引用手册内容解答，大幅提升培训效率与体验。`,
    bgcolor: 'rgba(255, 247, 229, 1)',
    // link: academyLinks['ai-script'],
    linkDesc: 'AI文生文，搞定专业脚本创作',
    imgAlt: '有言提供AI优化视频脚本的功能',
  },
];
</script>
<style lang="scss">
.section-industry {
  .marquee-slide .desc {
    height: 160px;
  }
  .industry-container {
    height: 712px;
  }
  .gsap-card {
    height: 618px !important;
  }
}
@media (max-width: $media-mobile) {
  .section-industry {
    .section-content {
      width: 100%;
    }
    padding-left: 0;
    padding-right: 0;
  }
  .industry-container {
    width: 100%;
    height: 461px;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }
}

@media (min-width: $media-pc) {
  .section-industry {
    .section-content {
      // width: calc(100vw - 24px);
      width: 100%;
    }
    // padding-left: 0 !important;
    // padding-right: 0 !important;
  }
  .industry-container {
    width: 100%;
    height: 750px;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }
}
</style>
<style lang="scss" scoped>
.section-industry--footer {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 40px;

  .start-btn,
  .book-btn {
    width: 178px;
    height: 48px;
  }
}
</style>
